import { PRIMARY } from '../../Theme/palette';
import { DrawerCloseButton, SharedForm, TimeRangeSelect } from '@components';
import { useStore } from '@stores';
import { TimeOption } from '@utils';
import {
  Alert,
  Button,
  Col,
  ConfigProvider,
  DatePicker,
  Drawer,
  Form,
  FormInstance,
  Input,
  Row,
  Segmented,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { observer } from 'mobx-react';
import { UnavailabilityFormValues, UnavailabilityMode } from './UnavailabilityDrawer.container';
import { UnavailabilityTable } from './UnavailabilityTable';
import './UnavailabilityDrawer.css';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const MODE_OPTIONS: { label: string; value: UnavailabilityMode }[] = [
  { label: 'Break', value: 'break' },
  { label: 'Full day', value: 'fullDay' },
  { label: 'Date range', value: 'range' },
];

const SEGMENTED_THEME = {
  components: { Segmented: { itemSelectedBg: PRIMARY, itemSelectedColor: '#fff' } },
};

interface IProps {
  form: FormInstance<UnavailabilityFormValues>;
  name?: string;
  userId?: number;
  workStationId?: string;
  day: Dayjs;
  isPastDay: boolean;
  mode: UnavailabilityMode;
  timeOptions: TimeOption[];
  hasWorkingHours: boolean;
  workingHoursLoading: boolean;
  setMode: (value: UnavailabilityMode) => void;
  setClose: () => void;
  handleSubmit: (values: UnavailabilityFormValues) => Promise<void>;
}

const UnavailabilityDrawer: React.FC<IProps> = ({
  form,
  name,
  userId,
  workStationId,
  day,
  isPastDay,
  mode,
  timeOptions,
  hasWorkingHours,
  workingHoursLoading,
  setMode,
  setClose,
  handleSubmit,
}) => {
  const { unavailabilityStore } = useStore();
  const dayLabel = day.isSame(dayjs(), 'day')
    ? `today, ${day.format('DD/MM/YYYY')}`
    : day.format('DD/MM/YYYY');
  const subject = workStationId ? 'workstation' : userId ? 'team member' : 'garage';
  const title = workStationId
    ? 'Add workstation unavailability'
    : userId
      ? 'Add staff unavailability'
      : 'Add non working days';

  // What the picked mode needs before there is anything to save. Watched rather than validated on
  // submit: an enabled Save on an empty form only leads to a red message under a blank field.
  const breakTime = Form.useWatch('breakTime', form);
  const dateRange = Form.useWatch('dateRange', form);
  const hasInput =
    mode === 'fullDay'
      ? true
      : mode === 'break'
        ? !!breakTime?.startTime && !!breakTime?.endTime
        : !!dateRange?.[0] && !!dateRange?.[1];

  // break and full day always act on the day shown in the calendar, a past one cannot be blocked
  const saveDisabled =
    unavailabilityStore.loading || (mode !== 'range' && isPastDay) || !hasInput;

  const description =
    mode === 'range' ? (
      <>
        Below you can select the dates on which the {subject} will not be available.
        <br />
        All assigned job cards for those days will be unassigned, remember to reach out to the
        customers if needed.
      </>
    ) : (
      <>
        Below you can mark the {subject} as unavailable for {dayLabel}.
        <br />
        All assigned job cards for that period will be unassigned, remember to reach out to the
        customers if needed.
      </>
    );

  return (
    <Drawer
      open={true}
      width="min(800px, 100vw)"
      styles={{ wrapper: { maxWidth: '100vw' } }}
      onClose={setClose}
      title={`Manage ${name} unavailability`}
      placement="right"
      closeIcon={false}
      extra={<DrawerCloseButton onClick={setClose} />}
    >
      <Row>
        <Col span={24}>
          <Text strong>{title}</Text>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Text>{description}</Text>
        </Col>
      </Row>
      <br />
      <Row>
        <Col span={24}>
          <ConfigProvider theme={SEGMENTED_THEME}>
            <Segmented
              block
              size="large"
              className="unavailability-mode-switch"
              value={mode}
              options={MODE_OPTIONS}
              onChange={setMode}
            />
          </ConfigProvider>
        </Col>
      </Row>
      <br />
      <SharedForm hideButtons form={form} onFinish={handleSubmit}>
        <Row gutter={8}>
          {mode !== 'range' && isPastDay && (
            <Col span={24}>
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message={`${day.format('DD/MM/YYYY')} is in the past. Pick another day in the calendar, or use a date range.`}
              />
            </Col>
          )}
          {mode === 'fullDay' && (
            <Col span={24}>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`The whole of ${day.format('DD/MM/YYYY')} will be marked as unavailable.`}
              />
            </Col>
          )}
          {mode === 'break' && (
            <>
              {!hasWorkingHours && !workingHoursLoading && (
                <Col span={24}>
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`No working hours are set for ${day.format('dddd')}, so all times are available to pick.`}
                  />
                </Col>
              )}
              <Col span={24}>
                <Form.Item
                  required
                  name="breakTime"
                  rules={[
                    {
                      validator: (_, value) =>
                        value?.startTime && value?.endTime
                          ? Promise.resolve()
                          : Promise.reject(new Error('Please select a start and end time')),
                    },
                  ]}
                >
                  <TimeRangeSelect
                    options={timeOptions}
                    disabled={workingHoursLoading || isPastDay}
                  />
                </Form.Item>
              </Col>
            </>
          )}
          {mode === 'range' && (
            <Col span={24}>
              <Form.Item
                required
                name="dateRange"
                rules={[
                  { required: true, message: 'Please select a start and end date' },
                  {
                    validator(_, value) {
                      if (!value || !value[0] || !value[1]) {
                        return Promise.resolve();
                      }
                      return value[1].isAfter(value[0])
                        ? Promise.resolve()
                        : Promise.reject(new Error('End date must be after the start date'));
                    },
                  },
                ]}
              >
                <RangePicker
                  style={{ width: '100%' }}
                  showTime={{ format: 'HH:mm', minuteStep: 15 }}
                  format="DD/MM/YYYY HH:mm"
                  placeholder={['Select start date', 'Select end date']}
                  disabledDate={(current) => current < dayjs().startOf('day')}
                />
              </Form.Item>
            </Col>
          )}
          <Col span={24}>
            <Form.Item name="name">
              <Input placeholder="Reason (optional)" maxLength={200} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={unavailabilityStore.loading}
                disabled={saveDisabled}
              >
                Save
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </SharedForm>
      <Row>
        <Col span={24}>
          <Text>Scheduled unavailability</Text>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <UnavailabilityTable userId={userId} workStationId={workStationId} />
        </Col>
      </Row>
    </Drawer>
  );
};

export default observer(UnavailabilityDrawer);
